import { Link, createFileRoute } from "@tanstack/react-router";
import * as z from "zod/v4";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { HTTPError } from "ky";
import { api } from "@/lib/http";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { generateDefaultValue } from "@/lib/utils/zod";

export const Route = createFileRoute("/auth/forgot-password")({
  component: RouteComponent,
  staticData: {
    title: "Forgot Password",
  },
});

const ForgotPasswordFormSchema = z.object({
  email: z.email("Please enter a valid email").min(1, "Email is required"),
});
type ForgotPasswordFormSchema = z.infer<typeof ForgotPasswordFormSchema>;

function RouteComponent() {
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormSchema>({
    resolver: standardSchemaResolver(ForgotPasswordFormSchema),
    defaultValues: generateDefaultValue(ForgotPasswordFormSchema),
  });

  const forgotPassword = useMutation({
    mutationFn: api.auth.forgotPassword,
    onSuccess(_data, vars) {
      setSentToEmail(vars.email);
    },
    async onError(error: HTTPError) {
      const data = await error.response.json<{
        detail?: string;
        code?: string;
      }>();

      form.setError("root.serverError", {
        message: data.detail || "An unexpected error occurred",
      });
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      <div className="w-full space-y-6">
        {sentToEmail ? (
          <div className="space-y-4 text-center">
            <h1 className="text-4xl font-bold">Check your email</h1>
            <p className="text-base text-gray-500">
              We've sent a link to reset your password to{" "}
              <span className="font-medium text-gray-700">{sentToEmail}</span>.
            </p>
            <div className="mt-2 text-center">
              <Link
                to="/auth/signin"
                className="text-sm font-medium text-[#dd2590] hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2 text-center">
              <h1 className="text-4xl font-bold">Forgot password?</h1>
              <p className="text-base text-gray-500">
                Enter your email so we can send you a password reset link.
              </p>
            </div>

            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit((formData) => {
                  if (forgotPassword.isPending) return;
                  forgotPassword.mutate(formData);
                })}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="email"
                          placeholder="Email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <button
                  type="submit"
                  disabled={forgotPassword.isPending}
                  className="mb-0 w-full rounded-md bg-[#dd2590] py-3 font-medium text-white transition-colors hover:bg-pink-700 disabled:opacity-60"
                >
                  {forgotPassword.isPending ? "Sending..." : "Send reset link"}
                </button>

                {form.formState.errors.root?.serverError ? (
                  <p className="mt-2 text-sm text-destructive">
                    {form.formState.errors.root.serverError.message}
                  </p>
                ) : null}

                <div className="mt-2 text-center">
                  <Link
                    to="/auth/signin"
                    className="text-sm font-medium text-[#dd2590] hover:underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            </Form>
          </>
        )}
      </div>
    </main>
  );
}
